import React, { useEffect } from "react";
import { Button, Grid, MenuItem, TextField } from "@mui/material";
import { BaseTextField } from "components/FormBuilder/fields/BaseTextField";
import { Controller, useForm, useWatch } from "react-hook-form";

import AppForm from "components/Shared/AppForm";
import AppSwitch from "components/Shared/AppSwitch";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";

import { useAuth } from "hooks";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { CustomCountryPhoneInput } from "components/Shared/CustomCountryPhoneInput";

const INITIAL_STATE = {
  customerType: "Individual",
  organizationName: "",
  name: "",
  phone: "",
  email: "",
  vatPanNo: "",
  address: "",
  isActive: true,
  account: "",
  entity: "",
};

const CustomerModalForm = ({
  customerProfileDetail = null,
  isBusy = false,
  onSubmit = () => {},
  onCancel = () => {},
}) => {
  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      name: customerProfileDetail?.name || customerProfileDetail?.profileName || "",
      account: customerProfileDetail?.account || viewEntity?.entity?.account || "",
      entity: customerProfileDetail?.entity || viewEntity?.entity,
    },
  });

  const _watchAccount = useWatch({ control, name: "account" });
  const _watchCustomerType = useWatch({ control, name: "customerType" });
  const isBusiness = _watchCustomerType === "Business";

  // RTK Queries
  const { data: accounts, isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();

  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();

  useEffect(() => {
    if (isAccountAdmin || isSystemAdmin) {
      fetchEntities({
        account: _watchAccount?._id ?? viewEntity?.entity?.account?._id?._id,
      });
    }
  }, [
    _watchAccount?._id,
    fetchEntities,
    isAccountAdmin,
    isSystemAdmin,
    viewEntity?.entity?.account,
  ]);

  useEffect(() => {
    if (customerProfileDetail) {
      reset({
        ...INITIAL_STATE,
        ...customerProfileDetail,
        name: customerProfileDetail?.name || customerProfileDetail?.profileName || "",
      });
    } else {
      reset({
        ...INITIAL_STATE,
        account: viewEntity?.entity?.account || "",
        entity: viewEntity?.entity,
      });
    }
  }, [customerProfileDetail, reset, viewEntity?.entity]);

  const handleCustomerProfile = async (data) => {
    try {
      const payload = {
        ...data,
        name: data.name,
        account: data?.account?._id || data?.account,
        entity: data?.entity?._id || data?.entity,
      };
      await onSubmit(payload);
    } catch (error) {
      console.error("Error processing customer profile:", error);
    }
  };

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={2}>
          {(isAccountAdmin || isSystemAdmin) && (
            <>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="account"
                  control={control}
                  rules={{ required: "Please select an account." }}
                  render={({ field: { value, onChange } }) => (
                    <BaseAutocomplete
                      fullWidth
                      label="Account"
                      required
                      value={value || ""}
                      onChange={(_, data) => {
                        onChange(data);
                        setValue("entity", "");
                      }}
                      getOptionLabel={(option) => option?.name || ""}
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      options={accounts || []}
                      loading={loadingAccounts}
                      loadingText="Loading Account..."
                      disabled={!!customerProfileDetail || isAccountAdmin}
                      helperText={errors?.account?.message}
                      error={!!errors?.account}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="entity"
                  control={control}
                  rules={{ required: "Please select an entity." }}
                  render={({ field: { value, onChange } }) => (
                    <BaseAutocomplete
                      fullWidth
                      label="Entity"
                      required
                      value={value || ""}
                      onChange={(_, data) => onChange(data)}
                      getOptionLabel={(option) => option?.name || ""}
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      options={entities || []}
                      loading={loadingEntities}
                      loadingText="Loading Entities..."
                      disabled={!!customerProfileDetail}
                      helperText={errors?.entity?.message}
                      error={!!errors?.entity}
                    />
                  )}
                />
              </Grid>
            </>
          )}

          <Grid item xs={6}>
            <Controller
              name="name"
              control={control}
              rules={{ required: "Please provide full Name." }}
              render={({ field }) => (
                <BaseTextField
                  {...field}
                  required
                  fullWidth
                  label="Full Name"
                  placeholder="Enter Full Name"
                  error={!!errors?.name}
                  helperText={errors?.name?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="address"
              control={control}
              rules={{ required: "Please provide address." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Address"
                  error={!!errors.address}
                  helperText={errors?.address?.message}
                />
              )}
            />
          </Grid>

          <Grid item md={6}>
            <Controller
              name="phone"
              control={control}
              rules={{
                required: "Phone is required",
                validate: (value) => {
                  const digits = value.replace(/\D/g, "");
                  if (digits.length < 7 || digits.length > 17) {
                    return "Phone number must be between 9 and 16 digits";
                  }
                  return true;
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <CustomCountryPhoneInput
                  placeholder="Phone Number"
                  label="Phone"
                  {...field}
                  lockedCountry={{ idd: "+977", cca2: "NP" }}
                  error={!!error}
                  required
                  helperText={error?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <BaseTextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Email"
                  type="email"
                  error={!!errors.email}
                  helperText={errors?.email?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="customerType"
              control={control}
              rules={{ required: "Please select a customer type." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  required
                  fullWidth
                  size="small"
                  label="Customer Type"
                  error={!!errors?.customerType}
                  helperText={errors?.customerType?.message}
                >
                  <MenuItem value="Individual">Individual</MenuItem>
                  <MenuItem value="Business">Business</MenuItem>
                </TextField>
              )}
            />
          </Grid>

          {isBusiness && (
            <Grid item xs={6}>
              <Controller
                name="organizationName"
                control={control}
                rules={{
                  required: isBusiness
                    ? "Organization Name is required for Business."
                    : false,
                }}
                render={({ field }) => (
                  <BaseTextField
                    {...field}
                    required
                    fullWidth
                    size="small"
                    label="Organization Name"
                    placeholder="Enter Organization Name"
                    error={!!errors?.organizationName}
                    helperText={errors?.organizationName?.message}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={6}>
            <Controller
              name="vatPanNo"
              control={control}
              rules={{
                required: isBusiness
                  ? "VAT / PAN No is required for Business."
                  : false,
              }}
              render={({ field }) => (
                <BaseTextField
                  {...field}
                  fullWidth
                  size="small"
                  label={isBusiness ? "VAT / PAN No *" : "VAT / PAN No"}
                  placeholder="Enter VAT / PAN No"
                  error={!!errors?.vatPanNo}
                  helperText={errors?.vatPanNo?.message}
                />
              )}
            />
          </Grid>

          {customerProfileDetail && (
            <Grid item xs={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}
        </Grid>
      </AppForm.Content>
      <AppForm.Actions>
        <Button disabled={isBusy} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit(handleCustomerProfile)}
        >
          {customerProfileDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default CustomerModalForm;
